import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { auth } from '../lib/firebase';
import { RecaptchaVerifier, signInWithPhoneNumber } from 'firebase/auth';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../App';

const PhoneAuth = ({ onSuccess, onCancel }) => {
  const { t } = useTranslation();
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [name, setName] = useState('');
  const [city, setCity] = useState('');
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [step, setStep] = useState('phone'); // 'phone', 'otp', 'profile'
  const [loading, setLoading] = useState(false);

  const setupRecaptcha = () => {
    if (typeof window === 'undefined') return; // safety for SSR

    if (!window.recaptchaVerifier) {
      window.recaptchaVerifier = new RecaptchaVerifier(auth,'recaptcha-container',{
          size: 'invisible',
          callback: () => {
            console.log('reCAPTCHA resolved');
          },
        });
    }
  };

  const sendOTP = async (e) => {
    e.preventDefault();
    if (phone.length !== 10) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }

    if (!auth) {
      console.error('Firebase auth is not initialized');
      toast.error('Login is temporarily unavailable. Please try again later.');
      return;
    }

    setLoading(true);
    try {
      setupRecaptcha();
      const phoneNumber = `+91${phone}`;
      const appVerifier = window.recaptchaVerifier;
      
      const result = await signInWithPhoneNumber(auth, phoneNumber, appVerifier);
      setConfirmationResult(result);
      setStep('otp');
      toast.success(t('auth.otpSent'));
    } catch (error) {
      console.error('Error sending OTP:', error);
      const code = error?.code || '';
      const msg = (error?.message || '').toLowerCase();
    
      const isRateLimited =
        code === 'auth/too-many-requests' ||
        code === 'auth/quota-exceeded' ||
        msg.includes('too-many-requests');
    
      toast.error(
        isRateLimited
          ? "We couldn’t send a code right now. Please try again in a few minutes."
          : 'Something went wrong while sending the code. Please wait a moment and try again.'
      );
      if (window.recaptchaVerifier) {
        window.recaptchaVerifier.clear();
        window.recaptchaVerifier = null;
      }
    } finally {
      setLoading(false);
    }
  };

  const verifyOTP = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) {
      toast.error('Please enter a valid 6-digit OTP');
      return;
    }

    if (!confirmationResult) {
      toast.error('OTP session expired. Please request a new OTP.');
      setStep('phone');
      return;
    }

    setLoading(true);
    try {
      // REAL Firebase verification
      const result = await confirmationResult.confirm(otp);
      const user = result.user;
      // Check if user exists in backend
      try {
        const formData = new FormData();
        formData.append('phone', `+91${phone}`);
        const loginRes = await axios.post(`${API}/auth/login`, formData);
        onSuccess(loginRes.data.token, loginRes.data.user);
        toast.success(t('msg.loginSuccess'));
      } catch (err) {
        // User doesn't exist, show profile form
        setStep('profile');
      }
    } catch (error) {
      console.error('Error verifying OTP:', error);
      toast.error(t('msg.error') || 'Invalid OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const completeRegistration = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const res = await axios.post(`${API}/auth/register`, {
        phone: `+91${phone}`,
        name,
        city
      });
      
      onSuccess(res.data.id, res.data);
      toast.success(t('msg.regSuccess'));
    } catch (error) {
      console.error('Registration error:', error);
      toast.error('Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div id="recaptcha-container"></div>
      
      {step === 'phone' && (
        <form onSubmit={sendOTP} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Phone Number</label>
            <div className="flex">
              <span className="inline-flex items-center px-3 border border-r-0 border-gray-300 bg-gray-50 text-gray-500 rounded-l-md">
                +91
              </span>
              <Input
                data-testid="phone-input"
                type="tel"
                placeholder="Enter 10-digit number"
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                className="rounded-l-none"
                required
              />
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              data-testid="send-otp-btn"
              type="submit"
              className="flex-1"
              disabled={loading}
            >
              {loading ? 'Sending...' : 'Send OTP'}
            </Button>
            <Button
              data-testid="cancel-auth-btn"
              type="button"
              variant="outline"
              onClick={onCancel}
            >
              Cancel
            </Button>
          </div>
        </form>
      )}

      {step === 'otp' && (
        <form onSubmit={verifyOTP} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Enter OTP</label>
            <Input
              data-testid="otp-input"
              type="text"
              placeholder="6-digit OTP"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              maxLength={6}
              required
            />
            <p className="text-xs text-gray-500 mt-1">OTP sent to +91{phone}</p>
          </div>
          <div className="flex gap-2">
            <Button
              data-testid="verify-otp-btn"
              type="submit"
              className="flex-1"
              disabled={loading}
            >
              {loading ? 'Verifying...' : 'Verify OTP'}
            </Button>
            <Button
              data-testid="resend-otp-btn"
              type="button"
              variant="outline"
              onClick={() => {
                setStep('phone');
                setOtp('');
                setConfirmationResult(null);
                if (window.recaptchaVerifier) {
                window.recaptchaVerifier.clear();
                window.recaptchaVerifier = null;
                }
              }}
            >
              Change Number
            </Button>
          </div>
        </form>
      )}

      {step === 'profile' && (
        <form onSubmit={completeRegistration} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Name</label>
            <Input
              data-testid="name-input"
              type="text"
              placeholder="Your full name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">City</label>
            <Input
              data-testid="city-input"
              type="text"
              placeholder="Your city"
              value={city}
              onChange={(e) => setCity(e.target.value)}
            />
          </div>
          <Button
            data-testid="complete-registration-btn"
            type="submit"
            className="w-full"
            disabled={loading}
          >
            {loading ? 'Completing...' : 'Complete Registration'}
          </Button>
        </form>
      )}
    </div>
  );
};

export default PhoneAuth;